import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import Stack from '@mui/material/Stack';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthenticationFlowPageProps } from '../AuthenticationFlow';
import { PhoneNumberInput } from 'src/components/PhoneNumberInput';
import theme from 'src/styles/theme';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import type { CountryData } from 'react-phone-input-2';
import type { StoredPhoneNumberData } from '../store/pageDataTypes';
import { markPhoneForValidation, setPhone } from '../store/authSlice';

type AuthenticationEnterPhonePageProps = AuthenticationFlowPageProps;

export type AuthenticationEnterPhonePageUserData = {
  country: string;
  number: string;
};

export type StoredPhoneNumberDataWithValidity = StoredPhoneNumberData & {
  validFormat: boolean;
};

export function AuthenticationEnterPhonePage({
  pageId: pageID,
  onPageComplete,
}: AuthenticationEnterPhonePageProps) {
  const initialPhoneNumberData = useSelector((state: RootState) => state.phone);
  const dispatch = useDispatch();

  const phoneNumberData = useRef<StoredPhoneNumberDataWithValidity>({
    country: initialPhoneNumberData.country || 'us',
    countryDialCode: initialPhoneNumberData.countryDialCode || '1',
    phoneNumber: initialPhoneNumberData.phoneNumber || '',
    validFormat: false,
  });

  const [shouldShowError, setShouldShowError] = useState(false);
  // Used to refocus on the input after an unsuccessful submit attempt
  const phoneInputRef = useRef<HTMLInputElement | undefined>();
  const dropdownDisabled = true;

  const attemptSubmit = useCallback(async () => {
    if (phoneNumberData.current.validFormat) {
      setShouldShowError(false);
      const newPhoneData: StoredPhoneNumberData = {
        country: phoneNumberData.current.country,
        countryDialCode: phoneNumberData.current.countryDialCode,
        phoneNumber: phoneNumberData.current.phoneNumber,
      };
      dispatch(setPhone(newPhoneData));
      dispatch(markPhoneForValidation(newPhoneData));
      onPageComplete(pageID);
    } else {
      setShouldShowError(true);
      !!phoneInputRef.current && phoneInputRef.current.focus();
    }
  }, [onPageComplete, pageID, phoneNumberData, dispatch]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        attemptSubmit();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [attemptSubmit]);

  if (!pageID || !onPageComplete) return null;

  return (
    <Stack spacing={'32px'} my={'64px'} width={'100%'} maxWidth={'420px'}>
      <Box>
        <Typography
          variant="h4"
          fontWeight="500"
          textTransform={'capitalize'}
          textAlign={'center'}
        >
          Enter Phone Number
        </Typography>
      </Box>
      <Box>
        <PhoneNumberInput
          startingPhoneValue={phoneNumberData.current}
          dropdownDisabled={dropdownDisabled}
          phoneInputRef={phoneInputRef}
          onChangeHandler={(value: string, country: CountryData) => {
            const phoneNumber = value.slice(country.dialCode.length);
            phoneNumberData.current = {
              ...phoneNumberData.current,
              country: country.countryCode,
              countryDialCode: country.dialCode,
              phoneNumber: phoneNumber,
            };
          }}
          validityHandler={(validFormat: boolean) => {
            phoneNumberData.current = {
              ...phoneNumberData.current,
              validFormat: validFormat,
            };
          }}
        />
      </Box>
      <Box textAlign={'center'}>
        {shouldShowError && (
          <Typography
            variant="body2"
            color={theme.palette.error.main}
            textAlign={'center'}
            mb={2}
          >
            Invalid phone number format!
          </Typography>
        )}
        <LoadingButton
          variant="contained"
          size="large"
          type="submit"
          onClick={attemptSubmit}
        >
          Send Code
        </LoadingButton>
      </Box>
    </Stack>
  );
}
